/**
 * Filter für eingefügte Inhalte.
 *
 * Der Alltag in einer Kanzlei: Der Text entsteht in Word oder Outlook und
 * wird in den Newsletter kopiert. Ohne Filter landet dabei das komplette
 * Word-Markup in der Vorlage — `class="MsoNormal"`, `<o:p>`, bedingte
 * mso-Kommentare, Times New Roman, Schriftgrößen in Punkt und Farben wie
 * #C00000. Das Ergebnis: Der Newsletter bricht aus dem Designsystem aus, und
 * in Outlook sieht er anders aus als in der Vorschau.
 *
 * Dieser Filter behält, was der Inhalt ist (Absätze, Fett, Kursiv, Listen,
 * Links) und verwirft, was die Gestaltung ist (Schriftart, Größe, Farbe).
 * Die Gestaltung kommt aus dem Baustein, nicht aus der Zwischenablage.
 *
 * Bewusste Entscheidung gegen ein "formatiert einfügen": In einem Werkzeug,
 * das die CI verbindlich halten soll, wäre die häufigste Nutzung genau die
 * falsche.
 */

/** Tags, die inhaltlich etwas bedeuten und deshalb bleiben. */
const ERLAUBTE_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'A',
  'UL',
  'OL',
  'LI',
  'SPAN',
  'DIV',
]);

/**
 * Style-Eigenschaften, die eine AUSSAGE treffen (Hervorhebung) statt eine
 * Gestaltung vorzugeben. Alles andere fliegt raus.
 */
const ERLAUBTE_STILE = new Set(['font-weight', 'font-style', 'text-decoration']);

/**
 * Säubert aus der Zwischenablage eingefügtes HTML.
 *
 * @param {string} roh
 * @returns {string} HTML, das nur noch Inhalt trägt.
 */
export function eingefuegtesSaeubern(roh) {
  if (!roh) return '';

  // Bedingte Kommentare und XML-Inseln von Word zuerst entfernen - sie
  // enthalten ganze Stilblöcke und würden sonst als Text auftauchen.
  let html = roh
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\?xml[\s\S]*?\?>/gi, '')
    .replace(/<(style|script|meta|link|title)\b[\s\S]*?<\/\1>/gi, '')
    .replace(/<(style|meta|link)\b[^>]*>/gi, '')
    // Word-eigene Namensräume: <o:p>, <w:sdt>, <v:shape>
    .replace(/<\/?[a-z]+:[^>]*>/gi, '');

  // Über DOMParser statt innerHTML: der Inhalt stammt aus der Zwischenablage
  // und damit aus fremder Hand. DOMParser baut den Baum, ohne irgendetwas
  // auszuführen oder Ressourcen zu laden - selbst ein <img onerror="..."> aus
  // einer präparierten Quelle bleibt hier wirkungslos, und sein onerror wird
  // von attributeSaeubern ohnehin entfernt.
  const geparst = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const vorlage = geparst.body.firstElementChild;
  if (!vorlage) return '';

  saeubereKnoten(vorlage);

  // Leere Absätze, die Word gern hinterlässt.
  for (const absatz of [...vorlage.querySelectorAll('p, div')]) {
    const text = (absatz.textContent ?? '').replace(/ /g, ' ').trim();
    if (!text && !absatz.querySelector('img, br')) absatz.remove();
  }

  return vorlage.innerHTML.replace(/(\s*<br\s*\/?>\s*){3,}/gi, '<br /><br />').trim();
}

/**
 * Geht den Baum durch und entfernt alles, was nur Gestaltung ist.
 *
 * @param {Element} wurzel
 */
function saeubereKnoten(wurzel) {
  for (const knoten of [...wurzel.children]) {
    saeubereKnoten(knoten);

    if (!ERLAUBTE_TAGS.has(knoten.tagName)) {
      // Unbekanntes Element auflösen, den Inhalt aber behalten.
      knoten.replaceWith(...knoten.childNodes);
      continue;
    }

    attributeSaeubern(knoten);

    // Ein span ohne jede Auszeichnung ist reine Verpackung.
    if (
      (knoten.tagName === 'SPAN' || knoten.tagName === 'DIV') &&
      !knoten.getAttribute('style')
    ) {
      knoten.replaceWith(...knoten.childNodes);
    }
  }
}

/**
 * @param {Element} knoten
 */
function attributeSaeubern(knoten) {
  for (const name of [...knoten.getAttributeNames()]) {
    if (name === 'href' && knoten.tagName === 'A') {
      const ziel = knoten.getAttribute('href') ?? '';
      // Nur Verweise, die im Postfach etwas bewirken. javascript: und data:
      // haben in einer E-Mail nichts verloren.
      if (!/^(https?:|mailto:|tel:|\{\{)/i.test(ziel)) knoten.removeAttribute('href');
      continue;
    }
    if (name === 'style') {
      const behalten = (knoten.getAttribute('style') ?? '')
        .split(';')
        .map((teil) => teil.trim())
        .filter((teil) => {
          const [eigenschaft, wert] = teil.split(':').map((s) => s?.trim().toLowerCase());
          if (!eigenschaft || !wert) return false;
          if (!ERLAUBTE_STILE.has(eigenschaft)) return false;
          // "font-weight: normal" trägt keine Aussage.
          return !['normal', 'none', 'initial', 'inherit'].includes(wert);
        });

      if (behalten.length) knoten.setAttribute('style', behalten.join('; '));
      else knoten.removeAttribute('style');
      continue;
    }
    // class, lang, id, dir, width, align, data-* - alles Gestaltung oder Ballast.
    knoten.removeAttribute(name);
  }
}

/**
 * Hängt den Filter an den Texteditor von GrapesJS.
 *
 * Das Ereignis wird abgefangen (preventDefault) und der gesäuberte Inhalt
 * selbst eingesetzt - sonst würde der Browser sein Standardverhalten
 * ausführen und das Rohmarkup einfügen.
 *
 * @param {any} editor
 */
export function einfuegeFilterAnhaengen(editor) {
  /**
   * @param {ClipboardEvent} ereignis
   */
  const behandeln = (ereignis) => {
    const ablage = ereignis.clipboardData;
    if (!ablage) return;

    const html = ablage.getData('text/html');
    const text = ablage.getData('text/plain');
    if (!html && !text) return;

    ereignis.preventDefault();

    const eingesetzt = html
      ? eingefuegtesSaeubern(html)
      : // Reiner Text: Zeilenumbrüche erhalten, sonst klebt alles zusammen.
        text
          .split(/\r?\n\r?\n+/)
          .map((absatz) => absatz.replace(/\r?\n/g, '<br />'))
          .join('<br /><br />');

    const dokument = /** @type {Document} */ (
      /** @type {any} */ (ereignis.target)?.ownerDocument ?? document
    );
    // insertHTML ist veraltet, aber in contenteditable weiterhin der einzige
    // Weg, der die Rücknahme-Funktion des Editors intakt lässt.
    dokument.execCommand('insertHTML', false, eingesetzt);
  };

  // Der Editor arbeitet in einem eigenen Rahmen; dort muss der Filter hängen.
  editor.on('load', () => {
    const rahmen = editor.Canvas.getDocument();
    rahmen?.addEventListener('paste', behandeln, true);
  });

  // Nach jedem Rahmenwechsel erneut anhängen (z.B. Wechsel Rechner/Telefon).
  editor.on('canvas:frame:load', (/** @type {any} */ daten) => {
    const dokument = daten?.window?.document;
    dokument?.addEventListener('paste', behandeln, true);
  });
}
